/**
 * labCertificate — hoe een analysemonster heet op het certificaat van het lab.
 *
 * Het lab rapporteert per analysemonster onder één code, en die code kiezen wij: hij staat in
 * `<certificaat>` van het aanleverbestand en komt zo terug op het analysecertificaat. De vorm
 * ligt niet vast, dus je stelt hem per labopdracht in met een sjabloon.
 *
 * `{meetpunt}` is met opzet niet de aanbevolen vorm. Een mengmonster wordt samengesteld uit
 * potten van meerdere boringen; er ís dan niet één meetpunt. Kies je hem toch, dan worden de
 * namen samengevoegd (B01+B02), want een code die maar één van de boringen noemt liegt.
 *
 * Gedeeld tussen backoffice en backend: het blad op het scherm, de PDF en het aanleverbestand
 * horen dezelfde code te tonen, anders zoekt iemand straks een certificaat dat anders heet.
 */

/** Het sjabloon voor een nieuwe labopdracht. */
export const DEFAULT_CERTIFICATE_FORMAT = '{projectcode}/{analysemonster}';

/** De sjablonen die je kunt kiezen, in de volgorde van de keuzelijst. */
export const CERTIFICATE_FORMATS: { format: string; label: string; hint: string }[] = [
  {
    format: '{projectcode}/{meetpunt}/{analysemonster}',
    label: 'Projectcode / meetpunt / analysemonster',
    hint: 'Een mengmonster kan uit meerdere boringen komen; die namen worden dan samengevoegd.',
  },
  {
    format: '{projectcode}/{analysemonster}',
    label: 'Projectcode / analysemonster',
    hint: 'Aanbevolen: herkenbaar per project en uniek per analysemonster.',
  },
  {
    format: '{analysemonster}',
    label: 'Analysemonster',
    hint: 'Kort. Alleen bruikbaar als het lab de opdracht al aan je project koppelt.',
  },
];

/**
 * Het sjabloon zoals het in de keuzelijst staat.
 *
 * `{monsternaam}` was de oude naam van `{analysemonster}`. Opdrachten van voor die wijziging
 * dragen hem nog; ze horen dezelfde code te houden, dus vertalen we hem in plaats van hem te
 * laten vallen.
 */
export function normalizeCertificateFormat(format?: string | null): string {
  const value = String(format || '').trim();
  if (!value) return DEFAULT_CERTIFICATE_FORMAT;
  return value.replace(/\{monsternaam\}/gi, '{analysemonster}');
}

/**
 * De code voor één analysemonster.
 *
 * Lege stukken vallen weg in plaats van een gat achter te laten: zonder projectcode levert
 * `{projectcode}/{analysemonster}` gewoon `MM01` op, en niet `/MM01`.
 */
export function certificateCode({
  format,
  projectCode = '',
  measurementPoints = [],
  sampleName = '',
}: {
  format?: string | null;
  projectCode?: string;
  measurementPoints?: string[];
  sampleName?: string;
}): string {
  const points = [...new Set(measurementPoints.filter(Boolean))].join('+');
  const parts = normalizeCertificateFormat(format)
    .split('/')
    .map((part) =>
      part
        .replace(/\{projectcode\}/gi, projectCode || '')
        .replace(/\{meetpunt\}/gi, points)
        .replace(/\{analysemonster\}/gi, sampleName || '')
        .trim(),
    )
    .filter(Boolean);
  return parts.join('/');
}
